import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { InvitationTrackResponse } from '../../Admin/Types/GrowthType';
import { getPublicInvitation, trackInvitationClick, trackInvitationOpen } from '../../Admin/Services/GrowthService';

type LoadState = 'loading' | 'ready' | 'error';

function InvitationLandingPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<LoadState>('loading');
  const [invitation, setInvitation] = useState<InvitationTrackResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setErrorMessage('This invitation link is incomplete. Please use the link in your invitation email.');
      setState('error');
      return;
    }

    const response = await getPublicInvitation(token);

    if (!response.success || !response.data) {
      setErrorMessage(response.message || 'This invitation is invalid or no longer available.');
      setState('error');
      return;
    }

    setInvitation(response.data);

    if (response.data.expired) {
      setErrorMessage('This invitation has expired. Please request a new invitation.');
      setState('error');
      return;
    }

    setState('ready');
    trackInvitationOpen(token);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const acceptAndRegister = async () => {
    if (!token || !invitation || submitting) return;

    setSubmitting(true);
    try {
      await trackInvitationClick(token);
    } finally {
      window.location.href = invitation.registrationUrl || `/registration/${token}`;
    }
  };

  const displayName = invitation?.firstName?.trim() || 'there';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 px-4 py-12 selection:bg-cyan-500/30 selection:text-cyan-200">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest text-cyan-300">
            Invitation to Nexgent
          </span>
          <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-indigo-500">
            Exclusive Early Access
          </h1>
          <p className="mt-2 text-slate-400 text-sm font-medium tracking-wide">
            By invitation only · The Executive Network
          </p>
        </div>

        <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-[2rem] shadow-[0_0_60px_-15px_rgba(6,182,212,0.3)] p-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />

          {state === 'loading' && (
            <div className="py-10 text-center">
              <div className="mx-auto w-10 h-10 border-2 border-slate-700 border-t-cyan-400 rounded-full animate-spin" />
              <p className="mt-4 text-slate-400 text-sm">Validating your invitation…</p>
            </div>
          )}

          {state === 'error' && (
            <div className="py-6 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-rose-500/10 border border-rose-500/40 flex items-center justify-center text-rose-400 text-xl font-bold">
                !
              </div>
              <h2 className="mt-4 text-lg font-bold text-white">Invitation Unavailable</h2>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{errorMessage}</p>
              <Link
                to="/"
                className="mt-6 inline-block text-sm font-semibold text-cyan-400 hover:text-cyan-300 underline underline-offset-4"
              >
                Back to home
              </Link>
            </div>
          )}

          {state === 'ready' && invitation && (
            <div>
              <div className="text-center">
                <p className="text-slate-400 text-sm">You have been personally invited by the Nexgent team</p>
                <h2 className="mt-3 text-2xl font-bold text-white">
                  Welcome, {displayName}
                  {invitation.currentTitle && (
                    <span className="block mt-1 text-sm font-medium text-cyan-300">
                      {invitation.currentTitle}{invitation.company ? ` · ${invitation.company}` : ''}
                    </span>
                  )}
                </h2>
              </div>

              <div className="mt-6 space-y-3 rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 text-cyan-400">◆</span>
                  <p className="text-sm text-slate-300">
                    Skip the waitlist and create your executive profile directly.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 text-fuchsia-400">◆</span>
                  <p className="text-sm text-slate-300">
                    Your invitation is tied to this email — register with it to unlock access.
                  </p>
                </div>
                {invitation.expiresAt && (
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 text-indigo-400">◆</span>
                    <p className="text-sm text-slate-300">
                      Valid until{' '}
                      <span className="font-semibold text-white">
                        {new Date(invitation.expiresAt).toLocaleString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              <button
                onClick={acceptAndRegister}
                disabled={submitting}
                className="mt-6 w-full bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-indigo-500 hover:opacity-95 disabled:opacity-60 text-white font-black tracking-wide py-4 rounded-2xl transition-all shadow-xl shadow-fuchsia-500/20 border border-white/10 disabled:cursor-not-allowed"
              >
                {submitting ? 'Opening…' : invitation.accepted ? 'Continue to Registration' : 'Accept Invitation & Register'}
              </button>

              <p className="mt-4 text-center text-xs text-slate-500">
                Already have an account?{' '}
                <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-semibold">
                  Sign in
                </Link>
              </p>
            </div>
          )}
        </div>

        <div className="mt-8 text-center text-xs text-slate-600 font-semibold tracking-wider uppercase">
          &copy; 2026 Reverse Job Portal
        </div>
      </div>
    </div>
  );
}

export default InvitationLandingPage;
